import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useAttendance } from "@/hooks/useAttendance";
import { usePointRanking } from "@/hooks/usePointRanking";
import { useTheme } from "@/hooks/ThemeContext";
import { useNotifications } from "@/hooks/useNotifications";
import { navItems } from "@/mocks/homeData";
import { useSiteLogo } from "@/hooks/useSiteLogo";

const dropdownMenus: Record<string, { label: string; href: string }[]> = {
  먹튀검증: [
    { label: "검증 프로세스", href: "/verification" },
    { label: "먹튀 제보하기", href: "/verification/report" },
    { label: "보증업체 보기", href: "/verified" },
  ],
  커뮤니티: [
    { label: "전체 게시판", href: "/community" },
    { label: "자유게시판", href: "/community/free" },
    { label: "홍보센터", href: "/community/promote" },
    { label: "월드컵픽스터", href: "/community/picks" },
  ],
  포인트존: [
    { label: "기프티콘교환", href: "/guide/gift" },
    { label: "포인트교환", href: "/guide/point" },
  ],
  스포츠정보: [
    { label: "전체 종목", href: "/sports" },
    { label: "축구 분석", href: "/sports/soccer" },
    { label: "야구 분석", href: "/sports/baseball" },
    { label: "농구 분석", href: "/sports/basketball" },
    { label: "배구 분석", href: "/sports/volleyball" },
  ],
  고객센터: [
    { label: "공지사항", href: "/notices" },
    { label: "이용가이드", href: "/guide" },
    { label: "포인트및경험치", href: "/level-guide" },
  ],
};

export default function Header() {
  const { user, signOut } = useAuth();
  const { checkedToday, checking, streakCount, doCheckIn } = useAttendance();
  const { toggleTheme, isDark } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  const displayName = user?.user_metadata?.nickname || user?.email?.split("@")[0] || "회원";
  const { logoUrl } = useSiteLogo();

  const {
    notifications,
    unreadCount,
    showDropdown: notifDropdownOpen,
    setShowDropdown: setNotifDropdownOpen,
    newToast,
    dismissToast,
    markAsRead,
    markAllAsRead,
  } = useNotifications(user?.id);

  function formatNotifDate(dateStr: string): string {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "방금 전";
    if (diffMins < 60) return `${diffMins}분 전`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}시간 전`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}일 전`;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }
    supabase
      .from("profiles")
      .select("role")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => setIsAdmin(data?.role === "admin"))
      .catch(() => setIsAdmin(false));
  }, [user]);

  const handleCheckIn = useCallback(async () => {
    if (checking || checkedToday) return;
    const result = await doCheckIn();
    setToastMessage(result.message);
    setTimeout(() => setToastMessage(""), 3000);
  }, [checking, checkedToday, doCheckIn]);

  return (
    <header className="w-full sticky top-0 z-50">
      <div className="bg-background-50/95 backdrop-blur-sm border-b border-background-300">
        <div className="max-w-[1460px] mx-auto pl-0 pr-3 md:pr-4">
          <div className="flex items-center justify-between py-2.5 md:py-3">
            {/* Logo */}
            <div className="flex items-center gap-1">
              <Link to="/" className="flex items-center gap-2 flex-shrink-0">
                <img
                  alt="월드컵티비 시즌2"
                  className="w-[9rem] h-[54px] rounded-lg"
                  src={logoUrl}
                />
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden md:flex items-center ml-3">
                {navItems.map((item) => {
                  const hasDrop = item.hasDropdown && dropdownMenus[item.label];
                  return (
                    <div
                      key={item.label}
                      className="relative"
                      onMouseEnter={() => hasDrop && setOpenDropdown(item.label)}
                      onMouseLeave={() => hasDrop && setOpenDropdown(null)}
                    >
                      {hasDrop ? (
                        <button
                          type="button"
                          className={`flex items-center gap-1 px-3 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer rounded ${
                            item.active
                              ? "text-primary-400 font-bold bg-primary-500/10"
                              : "text-foreground-600 hover:text-foreground-900 hover:bg-background-200"
                          }`}
                        >
                          <span>{item.label}</span>
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-arrow-down-s-line" />
                          </span>
                        </button>
                      ) : (
                        <Link
                          to={item.href}
                          className={`flex items-center gap-1 px-3 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer rounded ${
                            item.active
                              ? "text-primary-400 font-bold bg-primary-500/10"
                              : "text-foreground-600 hover:text-foreground-900 hover:bg-background-200"
                          }`}
                        >
                          <span>{item.label}</span>
                        </Link>
                      )}
                      {hasDrop && openDropdown === item.label && (
                        <div className="absolute left-0 top-full pt-1 z-20">
                          <div className="bg-background-100 border border-background-300 rounded-lg shadow-lg py-1 min-w-[160px]">
                            {dropdownMenus[item.label].map((sub) => (
                              <Link
                                key={sub.label}
                                to={sub.href}
                                className="block px-4 py-2 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-200 transition-colors whitespace-nowrap"
                                onClick={() => setOpenDropdown(null)}
                              >
                                {sub.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>

            {/* Desktop Auth Section */}
            <div className="hidden md:flex items-center gap-1 flex-shrink-0">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-background-300 text-foreground-600 transition-colors cursor-pointer"
                title={isDark ? "낮 모드로 전환" : "밤 모드로 전환"}
              >
                <i className={isDark ? "ri-sun-line" : "ri-moon-line"} />
              </button>

              {/* Notification Bell */}
              {user && (
                <div className="relative">
                  <button
                    onClick={() => setNotifDropdownOpen(!notifDropdownOpen)}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-background-300 text-foreground-600 transition-colors cursor-pointer relative"
                  >
                    <i className="ri-notification-3-line" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center px-1 text-[9px] font-bold bg-red-500 text-white rounded-full">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {notifDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setNotifDropdownOpen(false)}
                      />
                      <div className="absolute right-0 top-full mt-1 w-80 bg-background-100 border border-background-300 rounded-lg shadow-lg z-20">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-background-300">
                          <h4 className="text-xs font-bold text-foreground-800">알림</h4>
                          {unreadCount > 0 && (
                            <button
                              onClick={markAllAsRead}
                              className="text-[10px] text-foreground-600 hover:text-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              모두 읽음
                            </button>
                          )}
                        </div>
                        <div className="max-h-[360px] overflow-y-auto">
                          {notifications.length === 0 ? (
                            <div className="px-4 py-10 text-center">
                              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-background-200 flex items-center justify-center">
                                <i className="ri-notification-off-line text-foreground-600" />
                              </div>
                              <p className="text-xs text-foreground-600">알림이 없습니다</p>
                            </div>
                          ) : (
                            notifications.map((notif) => {
                              const isNoticeNotif = notif.type === "notice";
                              return (
                                <Link
                                  key={notif.id}
                                  to={isNoticeNotif && notif.related_id ? `/notices?id=${notif.related_id}` : "#"}
                                  onClick={() => {
                                    markAsRead(notif.id);
                                    setNotifDropdownOpen(false);
                                  }}
                                  className="block px-4 py-3 border-b border-background-300/60 last:border-0 hover:bg-background-200/60 transition-colors"
                                >
                                  <div className="flex items-start gap-2.5">
                                    <span className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-500/15 text-primary-400 mt-0.5">
                                      <i className={isNoticeNotif ? "ri-megaphone-line text-sm" : "ri-information-line text-sm"} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-foreground-700 truncate">
                                        {notif.title}
                                      </p>
                                      <p className="text-[11px] text-foreground-600 mt-0.5 line-clamp-1">
                                        {notif.message}
                                      </p>
                                      <p className="text-[10px] text-foreground-600 mt-1">
                                        {formatNotifDate(notif.created_at)}
                                      </p>
                                    </div>
                                  </div>
                                </Link>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Attendance Check Button */}
              {user && (
                <button
                  onClick={handleCheckIn}
                  disabled={checking || checkedToday}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                    checkedToday
                      ? "bg-accent-100/80 text-accent-700 cursor-default"
                      : "bg-accent-500 text-white hover:bg-accent-600"
                  } disabled:opacity-70`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={checkedToday ? "ri-check-double-fill" : "ri-calendar-check-line"} />
                  </span>
                  <span>{checkedToday ? `D+${streakCount}` : "출석체크"}</span>
                </button>
              )}

              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground-700 hover:text-foreground-900 transition-colors rounded-md hover:bg-background-200 whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-primary-400">
                      <i className="ri-user-fill" />
                    </span>
                    <span className="font-medium">{displayName}</span>
                    <span className="w-3 h-3 flex items-center justify-center text-foreground-600">
                      <i className="ri-arrow-down-s-line" />
                    </span>
                  </button>
                  {profileMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setProfileMenuOpen(false)}
                      />
                      <div className="absolute right-0 top-full mt-1 w-48 bg-background-100 border border-background-300 rounded-lg shadow-lg z-20 py-1">
                        <div className="px-4 py-2 border-b border-background-300">
                          <p className="text-xs font-bold text-foreground-800">{displayName}</p>
                          <p className="text-[10px] text-foreground-600 truncate">{user.email}</p>
                        </div>
                        <Link
                          to="/profile"
                          onClick={() => setProfileMenuOpen(false)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-xs text-foreground-600 hover:text-foreground-800 hover:bg-background-200 transition-colors whitespace-nowrap"
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-settings-3-line" />
                          </span>
                          내 정보
                        </Link>
                        <Link
                          to="/profile/exchanges"
                          onClick={() => setProfileMenuOpen(false)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-xs text-foreground-600 hover:text-foreground-800 hover:bg-background-200 transition-colors whitespace-nowrap"
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-exchange-line" />
                          </span>
                          내 교환내역
                        </Link>
                        {isAdmin && (
                          <Link
                            to="/admin"
                            onClick={() => setProfileMenuOpen(false)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-xs text-white hover:bg-accent-100/60 transition-colors whitespace-nowrap border-t border-background-300"
                          >
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-dashboard-line" />
                            </span>
                            관리자 대시보드
                          </Link>
                        )}
                        <button
                          onClick={() => { signOut(); setProfileMenuOpen(false); }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-logout-box-line" />
                          </span>
                          로그아웃
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-3 py-1.5 text-sm text-foreground-600 hover:text-foreground-800 transition-colors whitespace-nowrap"
                  >
                    로그인
                  </Link>
                  <Link
                    to="/register"
                    className="px-3 py-1.5 text-sm bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    회원가입
                  </Link>
                </>
              )}
            </div>

            {/* Mobile buttons */}
            <div className="flex md:hidden items-center gap-1.5">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 transition-colors cursor-pointer"
                title={isDark ? "낮 모드로 전환" : "밤 모드로 전환"}
              >
                <i className={isDark ? "ri-sun-line text-sm" : "ri-moon-line text-sm"} />
              </button>
              {user && (
                <div className="relative">
                  <button
                    onClick={() => setNotifDropdownOpen(!notifDropdownOpen)}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 transition-colors cursor-pointer relative"
                  >
                    <i className="ri-notification-3-line text-sm" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 flex items-center justify-center px-0.5 text-[8px] font-bold bg-red-500 text-white rounded-full">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </button>
                  {notifDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setNotifDropdownOpen(false)}
                      />
                      <div className="absolute right-0 top-full mt-1 w-72 bg-background-100 border border-background-300 rounded-lg shadow-lg z-20">
                        <div className="flex items-center justify-between px-3 py-2.5 border-b border-background-300">
                          <h4 className="text-[11px] font-bold text-foreground-800">알림</h4>
                          {unreadCount > 0 && (
                            <button
                              onClick={markAllAsRead}
                              className="text-[10px] text-foreground-600 hover:text-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              모두 읽음
                            </button>
                          )}
                        </div>
                        <div className="max-h-[280px] overflow-y-auto">
                          {notifications.length === 0 ? (
                            <div className="px-4 py-8 text-center">
                              <p className="text-[11px] text-foreground-600">알림이 없습니다</p>
                            </div>
                          ) : (
                            notifications.slice(0, 10).map((notif) => {
                              const isNoticeNotif = notif.type === "notice";
                              return (
                                <Link
                                  key={notif.id}
                                  to={isNoticeNotif && notif.related_id ? `/notices?id=${notif.related_id}` : "#"}
                                  onClick={() => {
                                    markAsRead(notif.id);
                                    setNotifDropdownOpen(false);
                                  }}
                                  className="block px-3 py-2.5 border-b border-background-300/60 last:border-0 hover:bg-background-200/60 transition-colors"
                                >
                                  <div className="flex items-start gap-2">
                                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-500/15 text-primary-400 mt-0.5">
                                      <i className={isNoticeNotif ? "ri-megaphone-line text-[10px]" : "ri-information-line text-[10px]"} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[11px] font-medium text-foreground-700 truncate">{notif.title}</p>
                                      <p className="text-[10px] text-foreground-600 mt-0.5 line-clamp-1">{notif.message}</p>
                                    </div>
                                  </div>
                                </Link>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              {user && (
                <button
                  onClick={handleCheckIn}
                  disabled={checking || checkedToday}
                  className={`flex items-center gap-1 px-2 py-1 text-[11px] rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                    checkedToday
                      ? "bg-accent-100/80 text-accent-700"
                      : "bg-accent-500 text-white"
                  }`}
                >
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className={checkedToday ? "ri-check-double-fill" : "ri-calendar-check-line"} />
                  </span>
                  <span>{checkedToday ? `D+${streakCount}` : "출석"}</span>
                </button>
              )}
              {user ? (
                <Link to="/profile" className="px-2.5 py-1 text-[11px] text-foreground-700 whitespace-nowrap">
                  {displayName}
                </Link>
              ) : (
                <Link to="/login" className="px-2.5 py-1 text-[11px] bg-primary-500 text-white rounded-md whitespace-nowrap">
                  로그인
                </Link>
              )}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="w-8 h-8 flex items-center justify-center text-foreground-700 cursor-pointer"
              >
                <i className="ri-menu-line text-lg" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {toastMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 bg-background-100 border border-accent-300/60 rounded-xl shadow-lg animate-[fadeIn_0.3s_ease-out]">
          <p className="text-sm font-medium text-foreground-700 whitespace-nowrap">{toastMessage}</p>
        </div>
      )}

      {/* Realtime Notification Toast */}
      {newToast && (
        <div className="fixed top-20 right-6 z-[60] max-w-sm animate-[slideInRight_0.4s_ease-out]">
          <div className="bg-background-100 border border-primary-400/30 rounded-xl shadow-lg p-4 flex items-start gap-3">
            <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-500/15 text-primary-400">
              <i className="ri-megaphone-line" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-foreground-800">{newToast.title}</p>
              <p className="text-[11px] text-foreground-600 mt-0.5 line-clamp-2">{newToast.message}</p>
            </div>
            <button
              onClick={dismissToast}
              className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-300">
          <div className="px-3 py-2 space-y-1">
            {navItems.map((item) => {
              const hasDrop = item.hasDropdown && dropdownMenus[item.label];
              return (
                <div key={item.label}>
                  {hasDrop ? (
                    <button
                      type="button"
                      className={`flex items-center gap-1 px-3 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer rounded w-full ${
                        item.active
                          ? "text-primary-400 font-bold bg-primary-500/10"
                          : "text-foreground-600 hover:text-foreground-900 hover:bg-background-200"
                      }`}
                    >
                      <span>{item.label}</span>
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line" />
                      </span>
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-1 px-3 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer rounded w-full ${
                        item.active
                          ? "text-primary-400 font-bold bg-primary-500/10"
                          : "text-foreground-600 hover:text-foreground-900 hover:bg-background-200"
                      }`}
                    >
                      <span>{item.label}</span>
                    </Link>
                  )}
                  {hasDrop && dropdownMenus[item.label] && (
                    <div className="ml-4 pl-3 border-l border-background-300 space-y-0.5">
                      {dropdownMenus[item.label].map((sub) => (
                        <Link
                          key={sub.label}
                          to={sub.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-3 py-1.5 text-xs text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {!user && (
              <div className="flex items-center gap-2 pt-2 border-t border-background-300 mt-2">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 py-2 text-sm text-center text-foreground-600 hover:text-foreground-800 transition-colors whitespace-nowrap"
                >
                  로그인
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 py-2 text-sm text-center bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  회원가입
                </Link>
              </div>
            )}
            {user && (
              <div className="pt-2 border-t border-background-300 mt-2">
                <p className="text-xs text-foreground-600 px-3 py-1">{user.email}</p>
                <Link
                  to="/profile/exchanges"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-200 rounded transition-colors whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-exchange-line" />
                  </span>
                  내 교환내역
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-accent-600 hover:text-accent-800 hover:bg-accent-100/60 rounded transition-colors whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-dashboard-line" />
                    </span>
                    관리자 대시보드
                  </Link>
                )}
                <button
                  onClick={() => { signOut(); setMobileMenuOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded transition-colors whitespace-nowrap cursor-pointer"
                >
                  로그아웃
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}